import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, Umbrella, ThermometerSun, Coffee, Users, TrendingUp,
  Check, X, RefreshCw, Download, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  Vacation: { bg: 'bg-blue-100', text: 'text-blue-700', bar: '#3b82f6' },
  Sick: { bg: 'bg-orange-100', text: 'text-orange-700', bar: '#f97316' },
  Personal: { bg: 'bg-purple-100', text: 'text-purple-700', bar: '#8b5cf6' },
  Other: { bg: 'bg-gray-100', text: 'text-gray-700', bar: '#6b7280' },
};

export default function PTOAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accruing, setAccruing] = useState(false);
  const [filter, setFilter] = useState('pending');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/pto/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load PTO analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleAccrual = async () => {
    setAccruing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/pto/accrue`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to run accrual');
    } finally {
      setAccruing(false);
    }
  };

  const handleUpdateRequest = async (personnelId, requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/personnel/${personnelId}/time-off/${requestId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Request ${status.toLowerCase()}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update request');
    }
  };

  const filteredRequests = data ? (
    filter === 'pending' ? data.pending_requests :
    filter === 'upcoming' ? data.upcoming_approved :
    data.all_requests
  ) : [];

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading PTO analytics...</div>;
  if (!data) return <div className="p-12 text-center text-muted-foreground">No data available</div>;

  const { summary, usage_trend } = data;
  const maxUsage = Math.max(...usage_trend.map(m => m.Vacation + m.Sick + m.Personal + m.Other), 1);

  return (
    <div className="space-y-6" data-testid="pto-analytics-page">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="p-4 text-center">
          <Users size={20} className="mx-auto mb-1 text-muted-foreground" />
          <p className="text-2xl font-bold">{summary.total_active_personnel}</p>
          <p className="text-xs text-muted-foreground">Active Personnel</p>
        </Card>
        <Card className="p-4 text-center">
          <TrendingUp size={20} className="mx-auto mb-1 text-muted-foreground" />
          <p className="text-2xl font-bold">{summary.personnel_with_pto}</p>
          <p className="text-xs text-muted-foreground">With PTO Setup</p>
        </Card>
        <Card className="p-4 text-center bg-blue-50 border-blue-200">
          <Umbrella size={20} className="mx-auto mb-1 text-blue-600" />
          <p className="text-2xl font-bold text-blue-600">{summary.total_vacation_balance}</p>
          <p className="text-xs text-muted-foreground">Vacation Days</p>
        </Card>
        <Card className="p-4 text-center bg-orange-50 border-orange-200">
          <ThermometerSun size={20} className="mx-auto mb-1 text-orange-600" />
          <p className="text-2xl font-bold text-orange-600">{summary.total_sick_balance}</p>
          <p className="text-xs text-muted-foreground">Sick Days</p>
        </Card>
        <Card className="p-4 text-center bg-purple-50 border-purple-200">
          <Coffee size={20} className="mx-auto mb-1 text-purple-600" />
          <p className="text-2xl font-bold text-purple-600">{summary.total_personal_balance}</p>
          <p className="text-xs text-muted-foreground">Personal Days</p>
        </Card>
        <Card className="p-4 text-center bg-yellow-50 border-yellow-200">
          <Clock size={20} className="mx-auto mb-1 text-yellow-600" />
          <p className="text-2xl font-bold text-yellow-600">{summary.pending_count}</p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </Card>
        <Card className="p-4 text-center bg-green-50 border-green-200">
          <Calendar size={20} className="mx-auto mb-1 text-green-600" />
          <p className="text-2xl font-bold text-green-600">{summary.upcoming_count}</p>
          <p className="text-xs text-muted-foreground">Upcoming</p>
        </Card>
      </div>

      {/* Accrual + Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={handleAccrual} disabled={accruing} data-testid="run-accrual-btn">
          <RefreshCw size={14} className={`mr-1 ${accruing ? 'animate-spin' : ''}`} />
          {accruing ? 'Running...' : 'Run Monthly Accrual'}
        </Button>
      </div>

      {/* Usage Trend Chart */}
      <Card className="p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} /> PTO Usage Trend (Last 6 Months)</h3>
        <div className="flex items-end gap-2 h-40">
          {usage_trend.map((month, i) => {
            const total = month.Vacation + month.Sick + month.Personal + month.Other;
            const h = total > 0 ? (total / maxUsage) * 100 : 2;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex flex-col-reverse rounded-t" style={{ height: `${h}%`, minHeight: '4px' }}>
                  {month.Vacation > 0 && <div style={{ height: `${(month.Vacation/total)*100}%`, background: '#3b82f6' }} className="rounded-t" />}
                  {month.Sick > 0 && <div style={{ height: `${(month.Sick/total)*100}%`, background: '#f97316' }} />}
                  {month.Personal > 0 && <div style={{ height: `${(month.Personal/total)*100}%`, background: '#8b5cf6' }} />}
                  {month.Other > 0 && <div style={{ height: `${(month.Other/total)*100}%`, background: '#6b7280' }} />}
                </div>
                <span className="text-xs text-muted-foreground">{month.month.slice(5)}</span>
                <span className="text-xs font-medium">{total > 0 ? total : ''}</span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 justify-center">
          {['Vacation', 'Sick', 'Personal'].map(t => (
            <span key={t} className="flex items-center gap-1 text-xs">
              <span className="w-3 h-3 rounded" style={{ background: TYPE_COLORS[t].bar }} />
              {t}
            </span>
          ))}
        </div>
      </Card>

      {/* Requests Table */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><Clock size={18} /> Time Off Requests</h3>
          <div className="flex gap-1">
            {[
              { key: 'pending', label: 'Pending', count: summary.pending_count },
              { key: 'upcoming', label: 'Upcoming', count: summary.upcoming_count },
              { key: 'all', label: 'All' },
            ].map(f => (
              <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'outline'}
                onClick={() => setFilter(f.key)} className="text-xs" data-testid={`pto-filter-${f.key}`}>
                {f.label} {f.count !== undefined && f.count > 0 && <Badge className="ml-1 text-[10px] px-1">{f.count}</Badge>}
              </Button>
            ))}
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No {filter} requests</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="p-2 font-medium">Name</th>
                  <th className="p-2 font-medium">Type</th>
                  <th className="p-2 font-medium">Dates</th>
                  <th className="p-2 font-medium text-center">Days</th>
                  <th className="p-2 font-medium">Status</th>
                  <th className="p-2 font-medium">Notes</th>
                  {filter === 'pending' && <th className="p-2 font-medium text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((req, i) => {
                  const tc = TYPE_COLORS[req.type] || TYPE_COLORS.Other;
                  return (
                    <tr key={req.id || i} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/personnel/${req.personnel_id}`)}>
                      <td className="p-2 font-medium">{req.name}</td>
                      <td className="p-2"><Badge className={`${tc.bg} ${tc.text} text-xs`}>{req.type}</Badge></td>
                      <td className="p-2 text-muted-foreground">{req.start_date} — {req.end_date}</td>
                      <td className="p-2 text-center">{req.days_requested}</td>
                      <td className="p-2">
                        <Badge className={
                          req.status === 'Approved' ? 'bg-green-100 text-green-700' :
                          req.status === 'Denied' ? 'bg-red-100 text-red-700' :
                          req.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100'
                        }>{req.status}</Badge>
                      </td>
                      <td className="p-2 text-muted-foreground text-xs max-w-[200px] truncate">{req.notes || ''}</td>
                      {filter === 'pending' && (
                        <td className="p-2 text-center" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
                              onClick={() => handleUpdateRequest(req.personnel_id, req.id, 'Approved')} data-testid={`approve-${req.id}`}>
                              <Check size={14} />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => handleUpdateRequest(req.personnel_id, req.id, 'Denied')} data-testid={`deny-${req.id}`}>
                              <X size={14} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
